/**
 * @file API service — contributorService.
 * Contributor Service
 * All API calls related to contributor details
 */

import axiosClient from "../api/axiosClient";
import { API_URLS } from "../constants/apiUrls";
import { FormDataResponse } from "../interfaces/formInterfaces";

/** Type definition — Contributor Profile. */
export interface ContributorProfile {
  affiliation?: string;
  background?: string;
  backgroundOther?: string;
  dataSource?: string;
}

/** Type definition — Contributor Payload. */
export interface ContributorPayload {
  affiliation?: string;
  background?: string;
  backgroundOther?: string;
  dataSource?: string;
  [key: string]: string | undefined;
}

/**
 * Get user profile
 */
export const getProfile = async () => {
  try {
    const response = await axiosClient.get(API_URLS.PROFILE_ME);
    return {
      success: true,
      data: response.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || "Failed to fetch profile",
      data: null,
    };
  }
};

/**
 * Get form data for contributor details stage
 */
export const getContributorFormData = async (
  stageCode: string
): Promise<{
  success: boolean;
  data: FormDataResponse | null;
  error?: string;
}> => {
  try {
    const response = await axiosClient.get(API_URLS.FORMS_STAGE(stageCode));
    return {
      success: true,
      data: response.data.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || "Failed to fetch form data",
      data: null,
    };
  }
};

/**
 * Get submission by ID
 */
export const getSubmission = async (
  submissionId: string
): Promise<{
  success: boolean;
  data: any | null;
  error?: string;
}> => {
  try {
    const response = await axiosClient.get(API_URLS.SUBMISSIONS.BY_ID(submissionId));
    return {
      success: true,
      data: response.data.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.message || "Failed to fetch submission",
      data: null,
    };
  }
};

/**
 * Save contributor details
 */
export const saveContributorDetails = async (
  submissionId: string,
  payload: ContributorPayload
): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> => {
  try {
    const response = await axiosClient.put(
      API_URLS.SUBMISSIONS.CONTRIBUTOR(submissionId),
      payload
    );
    return {
      success: true,
      message: response.data?.message || "Contributor details saved successfully",
    };
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to save contributor details";
    return {
      success: false,
      error: errorMessage,
    };
  }
};

